import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, CheckCircle, Eye, Send, X } from 'lucide-react'
import { callEdgeFunction } from '../../../lib/edgeFunctions'
import { nomDeLaCommunaute, type SemaineDuCentre } from '../../../lib/communication/communauteWhatsapp'

/**
 * Voir l'invitation WhatsApp d'une famille, et se l'envoyer, sans écrire à
 * aucune famille.
 *
 * L'aperçu montre le courriel exact qu'une famille recevra : même gabarit,
 * mêmes familles et mêmes groupes que le vrai lot. « M'envoyer ce test »
 * l'adresse à la personne connectée, et à elle seule, objet préfixé
 * « [TEST] ». Rien n'est écrit en base (`envoyer-invitations-whatsapp`).
 */

interface Famille {
  user_id: string
  libelle: string
}

interface Apercu {
  sujet: string
  html: string
  destinataire_reel: string
  famille: Famille
  familles: Famille[]
}

interface EnvoiDeTest {
  envoye_a: string
  sujet: string
  destinataire_reel: string
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : 'Aperçu impossible'
}

export function ApercuInvitationWhatsapp({ semaine, onClose }: { semaine: SemaineDuCentre; onClose: () => void }) {
  const communauteId = semaine.communaute?.id ?? ''
  const [apercu, setApercu] = useState<Apercu | null>(null)
  const [famille, setFamille] = useState<string | null>(null)
  const [chargement, setChargement] = useState(true)
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState<string | null>(null)

  const charger = useCallback(async (userId: string | null) => {
    setChargement(true)
    setErreur(null)
    setSucces(null)
    try {
      const a = await callEdgeFunction<Apercu>('envoyer-invitations-whatsapp', {
        communaute_id: communauteId,
        mode: 'apercu',
        user_id: userId ?? undefined,
      })
      setApercu(a)
      setFamille(a.famille.user_id)
    } catch (e) {
      setErreur(message(e))
    } finally {
      setChargement(false)
    }
  }, [communauteId])

  useEffect(() => {
    charger(null)
  }, [charger])

  const envoyer = async () => {
    if (!famille) return
    setEnvoi(true)
    setErreur(null)
    setSucces(null)
    try {
      const r = await callEdgeFunction<EnvoiDeTest>('envoyer-invitations-whatsapp', {
        communaute_id: communauteId,
        mode: 'test',
        user_id: famille,
      })
      setSucces(`Test envoyé à ${r.envoye_a} (« ${r.sujet} »). Aucune famille ne l'a reçu.`)
    } catch (e) {
      setErreur(message(e))
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col">
        <div className="flex items-start justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Eye className="h-5 w-5 mr-2" />
              Aperçu de l'invitation — {nomDeLaCommunaute(semaine)}
            </h3>
            <p className="text-sm text-gray-600">Aperçu sans envoi ; le test ne part qu'à vous.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 border-b border-gray-200">
          {apercu && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <label htmlFor="famille-whatsapp" className="text-gray-700">Famille :</label>
                <select
                  id="famille-whatsapp"
                  value={famille ?? ''}
                  onChange={e => charger(e.target.value)}
                  disabled={chargement || envoi}
                  className="border border-gray-300 rounded px-2 py-1 max-w-full"
                >
                  {apercu.familles.map(f => (
                    <option key={f.user_id} value={f.user_id}>{f.libelle}</option>
                  ))}
                </select>
                <span className="text-gray-500">{apercu.familles.length} famille(s) attendue(s)</span>
              </div>
              <div className="text-sm text-gray-700">
                <div><span className="font-medium">Objet :</span> {apercu.sujet}</div>
                <div><span className="font-medium">Partira à :</span> {apercu.destinataire_reel} (lors de l'envoi réel)</div>
              </div>
            </>
          )}
          {erreur && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800 flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>{erreur}</span>
            </div>
          )}
          {succes && (
            <div className="bg-green-50 border border-green-200 rounded p-2 text-sm text-green-800 flex items-start">
              <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>{succes}</span>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-hidden bg-gray-100">
          {chargement ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : apercu ? (
            // `sandbox` vide : ni script ni navigation, le courriel est montré, pas exécuté.
            <iframe title="Aperçu de l'invitation" sandbox="" srcDoc={apercu.html} className="w-full h-[60vh] bg-white" />
          ) : null}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">
            Fermer
          </button>
          <button
            onClick={envoyer}
            disabled={!apercu || chargement || envoi}
            className="flex items-center space-x-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {envoi ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="h-4 w-4" />}
            <span>M'envoyer ce test</span>
          </button>
        </div>
      </div>
    </div>
  )
}
